import { useState, useCallback } from 'react';
import { SessionHistoryItem, ChatMessage } from '../types';
import { chatService } from '../services/chatService';
import { logger } from '../utils/logger';
import { sessionDocToStreamEvent } from '../utils/sessionDocStreamEvent';

const moduleLogger = logger.getModule('useSessionHistory');

interface UseSessionHistoryReturn {
  historyItems: SessionHistoryItem[];
  isLoading: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: (sessionId: string) => Promise<void>;
  setHistoryItems: (items: SessionHistoryItem[]) => void;
  removeHistoryItem: (sessionId: string) => void;
  fetchSessionMessages: (currentSessionId: string, targetSessionId: string) => Promise<ChatMessage[]>;
  deleteSession: (currentSessionId: string, targetSessionId: string) => Promise<void>;
}

const HISTORY_PAGE_SIZE = 25;
const MAX_HISTORY_ITEMS = 1000;

export const useSessionHistory = (): UseSessionHistoryReturn => {
  const [historyItems, setHistoryItems] = useState<SessionHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);

  const loadMore = useCallback(async (sessionId: string) => {
    if (isLoading || !hasMore) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const nextPage = currentPage + 1;
      moduleLogger.debug('Loading more history', { sessionId, page: nextPage, limit: HISTORY_PAGE_SIZE });

      const response = await chatService.getSessionHistory(sessionId, nextPage, HISTORY_PAGE_SIZE);

      setHistoryItems((prev) => [...prev, ...response.sessions]);
      setCurrentPage(nextPage);
      setHasMore(response.has_more && ((nextPage + 1) * HISTORY_PAGE_SIZE) < MAX_HISTORY_ITEMS);

      moduleLogger.debug('History loaded', {
        newItems: response.sessions.length,
        total: response.total,
        hasMore: response.has_more
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load history';
      moduleLogger.error('Failed to load more history', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, hasMore, currentPage]);

  const setHistoryItemsWithState = useCallback((items: SessionHistoryItem[]) => {
    setHistoryItems(items);
    setCurrentPage(0); // Reset to first page when setting new items
    // Determine if more items might exist (if we got a full page)
    setHasMore(items.length >= HISTORY_PAGE_SIZE && items.length < MAX_HISTORY_ITEMS);
    setError(null);
    moduleLogger.debug('History items set', { count: items.length });
  }, []);

  const removeHistoryItem = useCallback((sessionId: string) => {
    setHistoryItems((prev) => prev.filter((item) => item.session_id !== sessionId));
    moduleLogger.debug('History item removed', { sessionId });
  }, []);

  const fetchSessionMessages = useCallback(async (currentSessionId: string, targetSessionId: string): Promise<ChatMessage[]> => {
    try {
      moduleLogger.debug('Fetching session messages', { currentSessionId, targetSessionId });
      const response = await chatService.getSessionMessages(currentSessionId, targetSessionId);
      
      // Helper function to strip context block from user messages
      // Context is prepended by the server in format: "context: {...}\n\nActual message"
      const stripContext = (content: string): string => {
        // Remove context block that starts with "context:" followed by JSON object and two newlines
        // Pattern: context: {...} at the beginning followed by \n\n
        return content.replace(/^context:\s*\{[\s\S]*?\}\s*\n\n/m, '').trim();
      };
      
      // Helper function to convert UTC timestamp to local time string
      const formatTimestamp = (dateString: string): string => {
        // Server sends UTC timestamps with 'Z' suffix, convert to local time
        const date = new Date(dateString);
        return date.toLocaleString(undefined, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      };
      
      // Convert ChatMessageDetail to ChatMessage format
      const messages: ChatMessage[] = response.messages.map((msg) => {
        const sessionDocEvents = (msg.session_docs || []).map(sessionDocToStreamEvent);

        return {
          id: msg.id.toString(),
          messageId: msg.id,
          role: msg.role as 'user' | 'assistant' | 'system',
          content: msg.role === 'user' ? stripContext(msg.content) : msg.content,
          sender: msg.role === 'user' ? 'You' : 'SpeedGauge',
          direction: msg.role === 'user' ? 'outgoing' as const : 'incoming' as const,
          timestamp: new Date(msg.created_at),
          citations: undefined,
          metadata: sessionDocEvents.length > 0
            ? { streamEvents: sessionDocEvents, streamEventType: 'response', streamPhase: 'complete' }
            : undefined
        };
      });
      
      return messages;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch session messages';
      moduleLogger.error('Failed to fetch session messages', err);
      throw new Error(errorMessage);
    }
  }, []);

  const deleteSession = useCallback(async (currentSessionId: string, targetSessionId: string): Promise<void> => {
    try {
      moduleLogger.debug('Deleting session', { currentSessionId, targetSessionId });
      await chatService.deleteHistorySession(currentSessionId, targetSessionId);
      removeHistoryItem(targetSessionId);
      moduleLogger.debug('Session deleted successfully', { targetSessionId });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete session';
      moduleLogger.error('Failed to delete session', err);
      throw new Error(errorMessage);
    }
  }, [removeHistoryItem]);

  return {
    historyItems,
    isLoading,
    hasMore,
    error,
    loadMore,
    setHistoryItems: setHistoryItemsWithState,
    removeHistoryItem,
    fetchSessionMessages,
    deleteSession
  };
};
